import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Mail, MailWarning, Trash2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const ENQ_STATUS = {
  new: { label: "New", cls: "bg-blue-100 text-blue-800" },
  contacted: { label: "Contacted", cls: "bg-violet-100 text-violet-800" },
  follow_up: { label: "Follow-up Required", cls: "bg-amber-100 text-amber-800" },
  converted: { label: "Converted", cls: "bg-emerald-100 text-emerald-800" },
  closed: { label: "Closed", cls: "bg-slate-200 text-slate-700" },
};
const fmtVal = (v) => (Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "Yes" : "No") : v || "—");

function StatusBadge({ s }) {
  const m = ENQ_STATUS[s] || { label: s, cls: "bg-secondary" };
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>{m.label}</span>;
}

function Stats({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {[["Total enquiries", stats.total, "enq-stat-total"], ["Last 7 days", stats.last_7d, "enq-stat-7d"], ["Converted", stats.converted, "enq-stat-converted"], ["New (unhandled)", stats.by_status.new, "enq-stat-new"]].map(([l, v, t]) => (
        <Card key={l} className="border-border/70"><CardContent className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{l}</div>
          <div className="mt-1 font-display text-2xl font-bold" data-testid={t}>{v}</div>
        </CardContent></Card>
      ))}
      {stats.by_course.length > 0 && (
        <Card className="border-border/70 sm:col-span-2 lg:col-span-4" data-testid="enq-course-stats"><CardContent className="p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Course-wise enquiries</div>
          <div className="flex flex-wrap gap-2">
            {stats.by_course.map((c) => (
              <span key={c.course} className="rounded-full border border-border px-3 py-1 text-xs"><b>{c.course}</b> · {c.count} ({c.converted} converted)</span>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

function Detail({ id, onClose, onChanged, courses }) {
  const [e, setE] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { setE(null); if (id) api.get(`/admin/enquiries/${id}`).then((r) => setE(r.data)); }, [id]);
  const update = async (body) => {
    try { const { data } = await api.put(`/admin/enquiries/${id}`, body); setE(data); onChanged(); toast.success("Updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addNote = async () => {
    const { data } = await api.post(`/admin/enquiries/${id}/notes`, { text: note }); setE(data); setNote(""); onChanged();
  };
  const resend = async () => { const { data } = await api.post(`/admin/enquiries/${id}/resend-email`); setE(data); onChanged(); toast(data.email_status === "sent" ? "Email sent" : `Email ${data.email_status}`); };
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="enquiry-detail-dialog">
        <DialogHeader><DialogTitle>{e ? e.name : "Loading…"}</DialogTitle></DialogHeader>
        {!e ? <Skeleton className="h-64" /> : (
          <div className="space-y-5">
            <div className="text-xs text-muted-foreground">Submitted {new Date(e.created_at).toLocaleString("en-IN")} {e.source_page && `· from ${e.source_page}`}</div>
            <div className="grid gap-x-6 sm:grid-cols-2">
              {e.answers.map((a) => (
                <div key={a.key} className="border-b border-dashed border-border py-2 text-sm">
                  <div className="text-xs text-muted-foreground">{a.label}</div><div className="break-words font-medium">{fmtVal(a.value)}</div>
                </div>
              ))}
            </div>
            <div className="grid gap-4 rounded-xl bg-secondary/50 p-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={e.status} onValueChange={(v) => update({ status: v })}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ENQ_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Resulted in enrollment?</Label>
                <div className="mt-2 flex items-center gap-2 text-sm"><Switch checked={!!e.converted} onCheckedChange={(v) => update({ converted: v })} data-testid="enquiry-converted-switch" /> {e.converted ? "Yes" : "No"}</div>
              </div>
              <div>
                <Label className="text-xs">Enrolled course</Label>
                <Select value={e.enrolled_course_id || ""} onValueChange={(v) => update({ enrolled_course_id: v, converted: true })}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="enquiry-enrolled-course"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">{e.email_status === "sent" ? <Mail className="h-3.5 w-3.5 text-emerald-600" /> : <MailWarning className="h-3.5 w-3.5 text-amber-600" />} Admin email: {e.email_status}{e.email_error && ` (${e.email_error})`}</span>
              <Button size="sm" variant="outline" onClick={resend} data-testid="enquiry-resend-email"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Resend notification</Button>
            </div>
            <div>
              <div className="text-sm font-semibold">Internal notes & follow-ups</div>
              <div className="mt-2 space-y-2" data-testid="enquiry-notes">
                {e.notes.map((n) => (
                  <div key={n.id} className="rounded-lg border border-border p-3 text-sm"><div className="text-xs text-muted-foreground">{n.by} · {new Date(n.at).toLocaleString("en-IN")}</div>{n.text}</div>
                ))}
                {!e.notes.length && <div className="text-xs text-muted-foreground">No notes yet.</div>}
              </div>
              <div className="mt-3 flex gap-2">
                <Textarea rows={2} value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="e.g. Called, asked to follow up next Monday" data-testid="enquiry-note-input" />
                <Button onClick={addNote} disabled={!note.trim()} data-testid="enquiry-add-note">Add</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function EnquiriesTab() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseOpts, setCourseOpts] = useState([]);
  const [f, setF] = useState({ q: "", course: "all", status: "all", date_from: "", date_to: "" });
  const [sel, setSel] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (f.q) p.set("q", f.q);
    if (f.course !== "all") p.set("course", f.course);
    if (f.status !== "all") p.set("status", f.status);
    if (f.date_from) p.set("date_from", f.date_from);
    if (f.date_to) p.set("date_to", f.date_to);
    return p.toString();
  }, [f]);
  const load = () => {
    api.get(`/admin/enquiries?${params}`).then((r) => setRows(r.data));
    api.get("/admin/enquiries/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/admin/courses").then((r) => setCourses(r.data));
    api.get("/admin/enquiry-form").then((r) => setCourseOpts(r.data.fields.find((x) => x.key === "course_interest")?.options || []));
  }, []);

  const exportCsv = async () => {
    const r = await api.get(`/admin/enquiries/export.csv?${params}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "pilotyatra-enquiries.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const del = async (id) => { if (!confirm("Delete this enquiry permanently?")) return; await api.delete(`/admin/enquiries/${id}`); load(); };
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  return (
    <div className="space-y-5" data-testid="enquiries-tab">
      <Stats stats={stats} />
      <Card className="border-border/70">
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={f.q} onChange={set("q")} placeholder="Search name, phone, email or course" className="pl-8" data-testid="enquiry-search" />
            </div>
            <Select value={f.course} onValueChange={set("course")}>
              <SelectTrigger className="w-48" data-testid="enquiry-filter-course"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All courses</SelectItem>{courseOpts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger className="w-48" data-testid="enquiry-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(ENQ_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={f.date_from} onChange={set("date_from")} className="w-40" data-testid="enquiry-date-from" />
            <Input type="date" value={f.date_to} onChange={set("date_to")} className="w-40" data-testid="enquiry-date-to" />
            <Button variant="outline" onClick={exportCsv} data-testid="enquiry-export-csv"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button>
          </div>
          {!rows ? <Skeleton className="h-40" /> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead>
                  <TableHead>Course</TableHead><TableHead>Status</TableHead><TableHead>Enrolled</TableHead><TableHead />
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((e) => (
                    <TableRow key={e.id} className="cursor-pointer" onClick={() => setSel(e.id)} data-testid={`enquiry-row-${e.id}`}>
                      <TableCell className="whitespace-nowrap text-xs">{new Date(e.created_at).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-xs">{e.phone}</TableCell>
                      <TableCell className="text-xs">{e.email}</TableCell>
                      <TableCell className="text-xs">{e.course || "—"}</TableCell>
                      <TableCell><StatusBadge s={e.status} /></TableCell>
                      <TableCell className="text-xs">{e.converted ? "Yes" : "—"}</TableCell>
                      <TableCell onClick={(ev) => ev.stopPropagation()}>
                        <Button size="icon" variant="ghost" onClick={() => del(e.id)} data-testid={`enquiry-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!rows.length && <TableRow><TableCell colSpan={8} className="py-8 text-center text-xs text-muted-foreground">No enquiries found.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <Detail id={sel} onClose={() => setSel(null)} onChanged={load} courses={courses} />
    </div>
  );
}
